/**
 * The site's L2 view (dashboard design rules §1): a `DetailDrawer` with the
 * site's raw values, its window-uptime meter with coverage, its heat row over
 * whatever window the board is scoped to, and a timeline of the degraded days
 * behind that row. Open state lives in the URL (`?site=<id>`) so the view is
 * shareable and the back button closes it (§4.2).
 *
 * Latency figures split by window on purpose: `Response` and the live avg/p50/
 * p95 come from the in-process ring (~1h), while the strip and timeline come
 * from the daily rollups — which hold means only, hence no historical p95.
 *
 * The timeline is derived from the same daily rollups the heat row shows —
 * NOVA keeps no per-incident event log for websites, so the honest event
 * history is "which days were below threshold, and how far".
 */

import { ExternalLink, Icon, TriangleAlert } from "@norican/icons";
import { Badge, EmptyState, Timeline, Typography } from "@norican/react";

import { DetailDrawer } from "@/components/board";
import type { MonitoringHistory, SiteStatus } from "@/lib/api";
import { useDrawerParam } from "@/lib/drill";

import {
  displayHost,
  driftTitle,
  formatDay,
  formatMs,
  formatPct,
  HEALTHY_PCT,
  redirectDrift,
  SHAKY_PCT,
  SITE_DETAIL_PARAM,
  STATUS_LABEL,
  STATUS_TONE,
} from "./status";
import { SiteHistoryStrip, SiteUptimeMeter } from "./uptime-heatmap";

/** How many degraded days the timeline lists before it stops (newest first). */
const TIMELINE_LIMIT = 8;

interface SiteDrawerProps {
  /** Every monitored site (the drawer looks its subject up by id). */
  sites: SiteStatus[];
  /** Daily rollups — the drawer renders the strip + timeline from these. */
  history?: MonitoringHistory;
}

export function SiteDrawer({ sites, history }: SiteDrawerProps) {
  const drawer = useDrawerParam(SITE_DETAIL_PARAM);
  const site = sites.find((candidate) => candidate.id === drawer.value);
  if (!site) {
    return null;
  }

  const siteHistory = history?.sites.find((candidate) => candidate.id === site.id);
  const drift = redirectDrift(site);

  const degradedDays = (siteHistory?.days ?? [])
    .filter((day) => day.checks && day.uptime_pct != null && day.uptime_pct < HEALTHY_PCT)
    .slice()
    .reverse();

  return (
    <DetailDrawer
      open
      onClose={drawer.close}
      title={site.name}
      breadcrumbs={[{ label: "Websites", to: "/websites" }]}
      items={[
        { label: "URL", value: displayHost(site.url) },
        // Only when the probe landed somewhere else — 0 hops is not a finding.
        ...(drift
          ? [
              {
                label: "Answers at",
                value: (
                  <span className="text-warning-fg text-sm" title={driftTitle(drift)}>
                    {drift.finalUrl} · {drift.hops} {drift.hops === 1 ? "redirect" : "redirects"}
                    {drift.schemeChanged ? " · scheme changed" : ""}
                  </span>
                ),
              },
            ]
          : []),
        { label: "Business", value: site.business },
        {
          label: "Status",
          value: (
            <Badge tone={STATUS_TONE[site.status]} variant="subtle" size="sm">
              {STATUS_LABEL[site.status]}
            </Badge>
          ),
        },
        {
          label: "Last check",
          value: site.last_error ? (
            <span className="text-danger-fg text-sm">{site.last_error}</span>
          ) : (
            `HTTP ${site.status_code ?? "—"}`
          ),
        },
        { label: "Response", value: formatMs(site.response_time_ms) },
        {
          // The window is in the label: these come from the live ring (~1h of
          // checks), not from the daily rollups the strip below is built on.
          label: "Latency, live window",
          value: (
            <span className="text-sm tabular-nums">
              avg {formatMs(site.avg_response_time_ms)} · p50 {formatMs(site.p50_response_time_ms)}{" "}
              · p95 {formatMs(site.p95_response_time_ms)}
            </span>
          ),
        },
        {
          label: "Availability",
          value: `${formatPct(site.uptime_pct)} over ${site.samples ?? 0} ${
            site.samples === 1 ? "check" : "checks"
          }`,
        },
      ]}
      timeline={
        degradedDays.length > 0 ? (
          <div className="flex flex-col gap-2">
            <Typography variant="body-sm" tone="muted">
              Degraded days ({Math.min(degradedDays.length, TIMELINE_LIMIT)}
              {degradedDays.length > TIMELINE_LIMIT ? ` of ${degradedDays.length}` : ""}, newest
              first)
            </Typography>
            <Timeline
              orientation="vertical"
              items={degradedDays.slice(0, TIMELINE_LIMIT).map((day) => ({
                id: day.day,
                tone:
                  (day.uptime_pct ?? 0) < SHAKY_PCT ? ("danger" as const) : ("warning" as const),
                icon: <Icon icon={TriangleAlert} size="sm" />,
                timestamp: formatDay(day.day),
                title: (
                  <span className="text-fg font-medium tabular-nums">
                    {day.uptime_pct?.toFixed(2)}% uptime
                  </span>
                ),
                description: (
                  <span className="text-fg-muted text-xs">
                    {day.checks} {day.checks === 1 ? "check" : "checks"} · avg{" "}
                    {formatMs(day.avg_response_time_ms)}
                  </span>
                ),
              }))}
            />
          </div>
        ) : (
          <EmptyState
            size="sm"
            title="No degraded days recorded"
            description={
              siteHistory
                ? `Every recorded day in the ${history?.days ?? 90}-day window was at or above ${HEALTHY_PCT}% uptime.`
                : "Daily rollups accrue from the first poll cycle onward."
            }
          />
        )
      }
      footer={
        <a
          href={site.url}
          target="_blank"
          rel="noreferrer noopener"
          className="text-accent inline-flex items-center gap-2 text-sm hover:underline"
        >
          Open {displayHost(site.url)}
          <Icon icon={ExternalLink} size="sm" />
        </a>
      }
    >
      {siteHistory && history && (
        <div className="flex flex-col gap-3">
          <SiteUptimeMeter site={siteHistory} days={history.days} />
          <SiteHistoryStrip site={siteHistory} days={history.days} />
        </div>
      )}
    </DetailDrawer>
  );
}
